import React, { ReactElement, useMemo } from "react";

import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import { ModalLabel } from "./ModalLabel";
import { cn } from "helpers/classNames";
import { TooltipInfo } from "./TooltipInfo";
import { guid } from "helpers/guid";

type Props<T extends FieldValues> =
  React.InputHTMLAttributes<HTMLInputElement> & {
    name: Path<T>;
    label?: string;
    error?: string;
    labelClassName?: string;
    appendix?: string;
    required?: boolean;
    render?: (field: ControllerRenderProps<T>) => ReactElement;
    disabled?: boolean;
    link?: string;
    linkLabel?: string;
    customSize?: "third" | "half" | "full";
    info?: string;
  };

export function TextInputFormProvider<T extends FieldValues>(props: Props<T>) {
  const {
    name,
    label,
    error,
    required,
    className,
    appendix,
    labelClassName,
    render,
    disabled = false,
    link,
    linkLabel,
    customSize,
    info,
    ...rest
  } = props;

  const { control, register, formState } = useFormContext<T>();

  const errorMessage = useMemo(() => {
    if (error) {
      return error;
    }

    const fieldError = formState.errors[name];
    return fieldError ? (fieldError.message as string) : undefined;
  }, [error, formState.errors, name]);

  const sizeClass = useMemo(() => {
    switch (customSize) {
      case "third":
        return "w-1/2 sm:w-1/3";
      case "half":
        return "sm:w-1/2";
      case "full":
        return "";
    }
  }, [customSize]);

  // const sizeClass =
  //   size === "third" ? "w-1/2 sm:w-1/3" : size === "half" ? "sm:w-1/2" : "";
  return (
    <div className={className}>
      {label && (
        <ModalLabel className={labelClassName}>
          {label}
          {required && "*"}
          {!!info && <TooltipInfo id={guid()} text={info} className="ml-1" />}
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-cyan-600 underline"
            >
              {linkLabel}
            </a>
          )}
        </ModalLabel>
      )}
      <div className={cn("relative mt-1 rounded-md shadow-sm", sizeClass)}>
        {render ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => render(field)}
          />
        ) : (
          <input
            {...register(name as Path<T>)}
            className={cn(
              "block w-full rounded-md text-base shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm",
              errorMessage
                ? "border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500"
                : "border-gray-300 focus:border-cyan-500 focus:ring-cyan-500",
            )}
            autoComplete="off"
            disabled={disabled}
            {...rest}
          />
        )}
        {!!errorMessage && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        )}
        {appendix && !errorMessage && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-gray-500 sm:text-sm">{appendix}</span>
          </div>
        )}
      </div>
      {!!errorMessage && (
        <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
}
